import axios from 'axios'
import { get } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { DirectUpload } from '@rails/activestorage'

import FlashMessages from '@services/FlashMessages'
import { formSyntaxToJson } from '@utils'

export default class DirectUploadService {
  _el = null
  _url = null
  _key = null
  _initialUrl = null
  _inputField = null
  _noDataMessage = null
  _resourceUrl = null
  _updateResponseResourceUrlPath = null
  _removePosition = null
  _loading = false
  _eventListeners = {}
  _canUpload = null
  _canRemove = null

  _initialHtml = null

  constructor (el, opts = {}) {
    this._uuid = uuid()
    this._el = el
    this._url = opts.uploadUrl || el.dataset.uploadUrl
    this._key = opts.uploadKey || el.dataset.uploadKey
    this._initialUrl = opts.initialUrl || el.dataset.initialUrl
    this._noDataMessage = opts.noDataMessage || el.dataset.noDataMessage
    this._removePosition = el.dataset.removePosition
    this._addPosition = el.dataset.addPosition
    this._updateResponseResourceUrlPath = opts.updateResponseResourceUrlPath || el.dataset.updateResponseResourceUrlPath
    this._initialHtml = this._el.innerHTML
    this._canUpload = !!el.dataset.allowUpload
    this._canRemove = !!el.dataset.allowDeleteUpload
    this.initialize()
  }

  get isLoading () {
    return this._loading
  }

  initialize () {
    this._resourceUrl = this._initialUrl && this._initialUrl !== 'false'
      ? this._initialUrl
      : null

    const anchor = this._el.querySelector('a[data-resource-target]')
    if (anchor) {
      anchor.href = this._resourceUrl
    }

    if (!this._el.classList.contains('direct-upload--initialized')) {
      this._setupFormElements()
    }
    this._determineVisibleState()

    this._el.classList.add('direct-upload--initialized')
  }

  toggleLoadingState () {
    if (this.isLoading) {
      this._el.classList.remove('loading')
    } else {
      this._el.classList.add('loading')
    }
    this._loading = !this._loading
    this._determineVisibleState()
  }

  updateSubElements () {
    const anchor = this._el.querySelector('a[data-resource-target]')
    if (anchor) {
      anchor.href = this._resourceUrl
    }

    const img = this._el.querySelector('img[data-resource-target]')
    if (img) {
      img.src = this._resourceUrl
    }
  }

  reset () {
    this._el.innerHTML = this._initialHtml
  }

  _setupFormElements () {
    const loadingMessage = document.createElement('div')
    loadingMessage.classList.add('direct-upload--loading-message')
    loadingMessage.innerHTML = 'Loading...'
    this._el.appendChild(loadingMessage)

    const noDataDiv = document.createElement('div')
    noDataDiv.classList.add('direct-upload--no-data-message')
    noDataDiv.innerHTML = this._noDataMessage
    this._el.appendChild(noDataDiv)

    if (this._canUpload) {
      const input = document.createElement('input')
      input.classList.add('direct-upload--file-input')
      input.name = this._key
      input.type = 'file'
      input.dataset.directUploadUrl = '/rails/active_storage/direct_uploads'
      this._inputField = input
      this._el.appendChild(input)

      const trigger = document.createElement('button')
      trigger.classList = [
        ...['direct-upload--upload-trigger'],
        ...(this._addPosition === 'top-right' && ['add-top-right'] || []),
        ...['inline-flex', 'items-center', 'py-1', 'border', 'text-sm',  'font-medium', 'rounded-md', 'focus:outline-0', 'transition', 'duration-150', 'ease-in-out'],
        ...['border-transparent', 'text-gray-200', 'hover:text-blue-500' ,'focus:outline-0'],
      ].join(' ')
      trigger.innerHTML = '<svg class="h-6 w-6 inline-block ml-2" fill="currentColor" height="24" viewBox="0 0 24 24" width="24"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-8-2h2v-4h4v-2h-4V7h-2v4H7v2h4z"/></svg>'
      this._el.appendChild(trigger)

      this._eventListeners.uploadTriggered = trigger.addEventListener('click', evt => {
        input.click()
      })

      this._eventListeners.fileInputChanged = input.addEventListener('change', evt => {
        if (input.files.length > 1) {
          FlashMessages.warn('You can only upload one file.')
        } else {
          this.toggleLoadingState()
          this._directUpload(input.files[0])
            .then(resp => {
              this._resourceUrl = get(resp.data, this._updateResponseResourceUrlPath)
              this.updateSubElements()
              this.toggleLoadingState()
              FlashMessages.success('File has been successfully uploaded.', 'Success')
            })
            .catch(resp => {
              this.toggleLoadingState()
              FlashMessages.error('File has failed to upload.', 'Failed')
            })
        }
      })
    }

    if (this._canRemove) {
      const removeButton = document.createElement('button')
      removeButton.classList = [
        ...['direct-upload--remove-trigger'],
        ...(this._removePosition === 'top-right' && ['remove-top-right'] || []),
        ...['inline-flex', 'items-center', 'py-1', 'border', 'text-sm', 'rounded-md', 'focus:outline-0', 'transition', 'duration-150', 'ease-in-out'],
        ...['border-transparent', 'text-gray-200', 'hover:text-red-500' ,'focus:outline-0'],
      ].join(' ')
      removeButton.innerHTML = '<svg class="h-4 w-4 inline-block" fill="none" stroke="currentColor" stroke-linecap="rounded" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24"><path fill-rule="none" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" clip-rule="evenodd"></path></svg>'
      this._el.appendChild(removeButton)

      this._eventListeners.removeTriggered = removeButton.addEventListener('click', evt => {
        let opts = formSyntaxToJson(this._key, null)
        let model_name = Object.keys(opts)[0]
        let attachment_name = Object.keys(opts[model_name])[0]
        opts[model_name]['attachment_to_delete']= attachment_name
        console.log(opts)
        if (confirm('Are you sure?')) {
          this.toggleLoadingState()
          axios.put(this._url, opts)
            .then(resp => {
              this._resourceUrl = null
              this.toggleLoadingState()
              FlashMessages.success('File has been successfully removed.', 'Success')
            })
            .catch(resp => {
              this.toggleLoadingState()
              FlashMessages.error('File has failed to be removed.', 'Failed')
            })
        }
      })
    }
  }

  _directUpload (file) {
    const upload = new DirectUpload(file, '/rails/active_storage/direct_uploads')
    return new Promise((resolve, reject) => {
      upload.create((err, blob) => {
        if (err) {
          reject(err)
        } else {
          axios.put(this._url, formSyntaxToJson(this._key, blob.signed_id))
            .then((resp) => {
              this._inputField.value = ''
              resolve(resp)
            })
            .catch((resp) => {
              this._inputField.value = ''
              reject(resp)
            })
        }
      })
    })
  }

  _determineVisibleState () {
    if (this._resourceUrl) {
      this._el.classList.remove('no-data')
    } else {
      this._el.classList.add('no-data')
    }
  }
}
